"use client";

import React, { useRef, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { SubPromotion } from '@/services/dataTypes';
import { getBaseImageUrl, getPromotionHref } from '@/constants/hooks';

interface Props {
    subPromotions: SubPromotion[];
    domain: string;
    promotionSlug: string;
}

const SubPromotionSlider = ({ subPromotions, domain, promotionSlug }: Props) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);

    const syncState = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        setCanPrev(t.scrollLeft > 4);
        setCanNext(t.scrollLeft < t.scrollWidth - t.clientWidth - 4);
    }, []);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        syncState();
        t.addEventListener('scroll', syncState, { passive: true });
        window.addEventListener('resize', syncState);
        return () => {
            t.removeEventListener('scroll', syncState);
            window.removeEventListener('resize', syncState);
        };
    }, [subPromotions, syncState]);

    const scroll = (dir: 'left' | 'right') => {
        trackRef.current?.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' });
    };

    if (!subPromotions?.length) return null;

    return (
        <div className="relative">
            {/* Prev button */}
            <button
                onClick={() => scroll('left')}
                disabled={!canPrev}
                aria-label="Previous"
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-9 h-9 rounded-full flex items-center justify-center bg-white transition-all duration-200 disabled:opacity-0 disabled:pointer-events-none hover:bg-[#5D27A1] hover:text-white focus:outline-none"
                style={{ boxShadow: '0 2px 12px rgba(74,154,110,0.15)', border: '1px solid #E8F5EE', color: '#5D27A1' }}
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Next button */}
            <button
                onClick={() => scroll('right')}
                disabled={!canNext}
                aria-label="Next"
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-9 h-9 rounded-full flex items-center justify-center bg-white transition-all duration-200 disabled:opacity-0 disabled:pointer-events-none hover:bg-[#5D27A1] hover:text-white focus:outline-none"
                style={{ boxShadow: '0 2px 12px rgba(74,154,110,0.15)', border: '1px solid #E8F5EE', color: '#5D27A1' }}
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Edge fades */}
            <div className="absolute left-0 top-0 bottom-0 w-10 z-10 pointer-events-none transition-opacity duration-200"
                style={{ background: 'linear-gradient(90deg, #ffffff, transparent)', opacity: canPrev ? 1 : 0 }} />
            <div className="absolute right-0 top-0 bottom-0 w-10 z-10 pointer-events-none transition-opacity duration-200"
                style={{ background: 'linear-gradient(270deg, #ffffff, transparent)', opacity: canNext ? 1 : 0 }} />

            {/* Track */}
            <div
                ref={trackRef}
                className="flex gap-4 overflow-x-auto pb-2 pt-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory' }}
            >
                {subPromotions.map((item) => (
                    <Link
                        key={item.unique_id}
                        href={getPromotionHref(item, promotionSlug)}
                        className="group relative flex-shrink-0 rounded-2xl overflow-hidden no-underline transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                        style={{
                            width: '240px',
                            height: '180px',
                            scrollSnapAlign: 'start',
                            border: '1px solid #E8F5EE',
                            boxShadow: '0 2px 10px rgba(74,154,110,0.06)',
                        }}
                    >
                        {/* Background image */}
                        {item.category_image ? (
                            <Image
                                src={getBaseImageUrl(domain, item.category_image, '')}
                                alt={item.category_name}
                                fill
                                unoptimized
                                className="object-cover transition-transform duration-500 group-hover:scale-105"
                                sizes="240px"
                            />
                        ) : (
                            <div className="absolute inset-0 bg-[#F7FBF9]" />
                        )}

                        {/* Gradient overlay */}
                        <div className="absolute inset-0"
                            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)' }} />

                        {/* Green accent bar on hover */}
                        <div className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#5D27A1]" />

                        {/* Content */}
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                            <h4 className="text-sm font-semibold leading-tight line-clamp-1 m-0 mb-1 text-white">
                                {item.category_name}
                            </h4>
                            {item.category_detail && (
                                <p className="text-[10px] line-clamp-1 m-0 mb-2" style={{ color: 'rgba(255,255,255,0.65)' }}>
                                    {item.category_detail}
                                </p>
                            )}
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300"
                                style={{ color: '#5D27A1' }}>
                                <span className="text-white">Explore</span>
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                </svg>
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default SubPromotionSlider;
